import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { iconsPath } from '../../../utils/imagePath';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetch_library_page,
  reset_library,
  fetch_sketchfab_page,
  reset_sketchfab,
} from '../../../redux/Slice/LiabrarySlice';
import { useParams, useNavigate } from 'react-router-dom';
import { CardLayout } from '../../../components/ui/UtilsLayout';
import Input from '../../../components/ui/Input';
import { LibraryCategories } from '../../../utils/constant';
import vector from '../../../assets/images/icons/Vector.svg';
import { ChevronDown } from 'lucide-react';
import MeshPreviewViewer from '../../../pages/Training/components/MeshPreviewViewer';
import CreateProjectModal from '../../../components/ProjectCreation/CreateNewProject';
import { sketchfabService } from '../../../utils/automation/sketchfabService';

const INDUSTRY_OPTIONS = [
  'Energy and Mining', 'Manufacturing & Industrial', 'Healthcare and Pharma',
  'Retail & E-Commerce', 'Education & Training', 'Automotive & Transportation',
  'Architecture & Real Estate', 'Defense & Aerospace', 'Tourism & Hospitality',
];
const CATEGORY_OPTIONS = ['Scene', 'Object', 'Character', 'Effect'];

const ASSET_SIZE_OPTIONS = [
  { label: '< 1 MB', hint: 'Mobile AR, WebAR, lightweight MR', tooltip: 'Optimised for fast loading on mobile and WebAR. Best for low-end devices, QR-based AR, UI elements, markers and low-poly 3D assets.', minBytes: 0, maxBytes: 1048576 },
  { label: '1 – 5 MB', hint: 'Mobile VR, Standalone VR, AR Training', tooltip: 'Suitable for mobile VR headsets and standalone devices. Good for interactive training modules and moderate-detail AR assets.', minBytes: 1048576, maxBytes: 5242880 },
  { label: '5 – 20 MB', hint: 'VR Training, MR Applications, PC Visualization', tooltip: 'Ideal for VR training environments and mixed reality applications on mid-range hardware. Supports higher polygon counts and richer textures.', minBytes: 5242880, maxBytes: 20971520 },
  { label: '20 – 100 MB', hint: 'High-Quality VR, PC Applications, Engineering Review', tooltip: 'High-fidelity assets for PC-based VR and engineering review sessions. Suitable for detailed architectural and industrial visualizations.', minBytes: 20971520, maxBytes: 104857600 },
  { label: '100 MB – 500 MB', hint: 'Advanced Simulations, Digital Twins, Defense/Industrial VR', tooltip: 'Advanced simulation-grade assets for digital twins, defense training and industrial-scale VR. Requires capable hardware.', minBytes: 104857600, maxBytes: 524288000 },
  { label: '500 Mb – 2GB', hint: 'Mobile AR, WebAR, lightweight MR', tooltip: 'Large-scale immersive environments. Typically used for high-end PC or tethered VR experiences with dense scene geometry.', minBytes: 524288000, maxBytes: 2147483648 },
  { label: '2GB – 5GB', hint: 'Mobile AR, WebAR, lightweight MR', tooltip: 'Very large scene packages for enterprise-grade simulations and photorealistic digital twins. Server-side streaming recommended.', minBytes: 2147483648, maxBytes: 5368709120 },
  { label: '5 GB', hint: 'Mobile AR, WebAR, lightweight MR', tooltip: 'Ultra-large assets exceeding 5 GB. Intended for offline or dedicated enterprise deployments with high-bandwidth requirements.', minBytes: 5368709120, maxBytes: Infinity },
];

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'spatialgrid', label: 'Spatial Grid' },
  { value: 'sketchfab', label: 'Sketchfab' },
];

const formatFileSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${Math.round(bytes / 1048576)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

const formatPolyCount = (count) => {
  if (count == null) return '—';
  if (count >= 1000) return `${Math.round(count / 1000)}k polygons`;
  return `${count} polygons`;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `Updated ${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Updated ${h}h ago`;
  return `Updated ${Math.floor(h / 24)}d ago`;
};

// ─── Info tooltip icon ───
const InfoTooltip = ({ text }) => {
  const [tooltipStyle, setTooltipStyle] = useState(null);
  const ref = useRef(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      const top = rect.top + rect.height / 2;
      if (spaceRight >= 240) {
        // place to the right
        setTooltipStyle({ top, left: rect.right + 10, transform: 'translateY(-50%)', side: 'right' });
      } else {
        // place to the left
        setTooltipStyle({ top, left: rect.left - 10, transform: 'translateY(-50%) translateX(-100%)', side: 'left' });
      }
    }
  };

  const handleMouseLeave = () => setTooltipStyle(null);

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center ml-1.5"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <svg className="w-3.5 h-3.5 text-[#888] cursor-help flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm.75 12h-1.5V7h1.5v5zM8 5.5a.875.875 0 1 1 0-1.75A.875.875 0 0 1 8 5.5z" />
      </svg>
      {tooltipStyle && createPortal(
        <div
          className="fixed z-[999999] w-56 rounded-xl bg-[#2e2e32] border border-[#3a3a3e] shadow-xl px-4 py-3 pointer-events-none"
          style={{ top: tooltipStyle.top, left: tooltipStyle.left, transform: tooltipStyle.transform }}
        >
          {tooltipStyle.side === 'left' ? (
            <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-[#2e2e32]" />
          ) : (
            <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-[#2e2e32]" />
          )}
          <p className="text-[12px] text-white leading-relaxed">{text}</p>
        </div>,
        document.body
      )}
    </span>
  );
};

// ─── Checkbox row ───
const CheckboxRow = ({ label, hint, tooltip, checked, onChange }) => (
  <label className="flex items-start gap-3 cursor-pointer" onClick={onChange}>
    <div className={`mt-0.5 flex-shrink-0 w-4 h-4 border rounded-sm flex items-center justify-center transition-colors ${checked ? 'bg-red-600 border-red-600' : 'border-[#666] bg-transparent'}`}>
      {checked && (
        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
    <div className="flex flex-col leading-tight">
      <span className="flex items-center text-[13px] text-white">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </span>
      {hint && <span className="text-[11px] text-[#888] mt-0.5">{hint}</span>}
    </div>
  </label>
);

// ─── Filter Modal ───
const FilterModal = ({ onClose, onApply, initialFilters }) => {
  const [industries, setIndustries] = useState(initialFilters?.industries || []);
  const [categories, setCategories] = useState(initialFilters?.categories || []);
  const [assetSizes, setAssetSizes] = useState(initialFilters?.assetSizes || []);
  const toggle = (_list, setList, value) =>
    setList((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4 md:items-start md:justify-end" onClick={onClose}>
      <div
        className="relative rounded-xl bg-[#1E1E22] border border-[#3A3A3E] shadow-2xl flex flex-col overflow-hidden
          w-full max-w-[95vw]
          sm:max-w-[560px]
          md:max-w-[720px]
          lg:max-w-[883px]
          md:mt-[120px] md:mr-4 lg:mr-6
          max-h-[85vh]"
        style={{ isolation: 'isolate' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header row with close button */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#2a2a2e]">
          <h3 className="text-[15px] font-semibold text-white">Filters</h3>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-[#555] text-[#aaa] hover:text-white hover:border-white transition-colors">✕</button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-5 lg:p-6">
          {/* On mobile/tablet: stacked layout; on lg+: 3 columns */}
          <div className="flex flex-col lg:flex-row gap-y-6 lg:gap-y-0">
            {/* Industry */}
            <div className="lg:w-[220px] lg:flex-shrink-0">
              <h3 className="text-[14px] sm:text-[15px] font-semibold text-white mb-3 sm:mb-4">Industry</h3>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                {INDUSTRY_OPTIONS.map((opt) => (
                  <CheckboxRow key={opt} label={opt} checked={industries.includes(opt)}
                    onChange={() => toggle(industries, setIndustries, opt)} />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px bg-[#2a2a2e] mx-6 self-stretch" />

            {/* Categories */}
            <div className="lg:w-[140px] lg:flex-shrink-0">
              <h3 className="text-[14px] sm:text-[15px] font-semibold text-white mb-3 sm:mb-4">Categories</h3>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                {CATEGORY_OPTIONS.map((opt) => (
                  <CheckboxRow key={opt} label={opt} checked={categories.includes(opt)}
                    onChange={() => toggle(categories, setCategories, opt)} />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px bg-[#2a2a2e] mx-6 self-stretch" />

            {/* Asset Size */}
            <div className="flex-1">
              <h3 className="text-[14px] sm:text-[15px] font-semibold text-white mb-3 sm:mb-4">Asset Size</h3>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                {ASSET_SIZE_OPTIONS.map((opt) => (
                  <CheckboxRow key={opt.label} label={opt.label} hint={opt.hint} tooltip={opt.tooltip}
                    checked={assetSizes.includes(opt.label)}
                    onChange={() => toggle(assetSizes, setAssetSizes, opt.label)} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#3A3A3E] px-4 sm:px-6 py-4 flex justify-end gap-3">
          <button type="button" onClick={() => { setIndustries([]); setCategories([]); setAssetSizes([]); }}
            className="px-4 sm:px-5 py-2 rounded-md border border-[#555] text-white text-[13px] hover:bg-[#2a2a2e] transition-colors">Reset</button>
          <button type="button" onClick={() => { onApply({ industries, categories, assetSizes }); onClose(); }}
            className="px-4 sm:px-5 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-[13px] font-medium transition-colors">Apply Filters</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Add to Project Modal ───
const AddToProjectModal = ({ asset, onClose }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmProject, setConfirmProject] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  const loadProjects = () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    fetch(
      `${process.env.REACT_APP_BASE_URL}experience?page=1&limit=100&sortBy=created_at&order=desc`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((res) => setProjects(res?.success ? (res.data?.data || res.data || []) : []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProjects(); }, []);

  const filtered = projects.filter((p) =>
    (p.project_name || p.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirmAdd = async () => {
    if (!confirmProject || adding) return;
    setAdding(true);
    try {
      const isSketchfab = asset.source === 'sketchfab';

      if (isSketchfab) {
        // Use sketchfabService to resolve the best GLB/GLTF download URL
        const downloadUrl = await sketchfabService.getModelDownloadUrl(asset.uid);

        // Navigate to GridPage — pass asset with url set so handleLibraryAssetImport can load it
        navigate(`/gridpage/${confirmProject._id}`, {
          state: {
            pendingLibraryAsset: {
              ...asset,
              url: downloadUrl,        // required by handleLibraryAssetImport → loadLibraryMesh
              title: asset.name,       // required by handleLibraryAssetImport for toast/display
              source: 'sketchfab',
            },
          },
        });
      } else {
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_BASE_URL}assets/library/add`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: confirmProject._id, libraryAssetId: asset._id }),
        });
        navigate(`/gridpage/${confirmProject._id}`, { state: { pendingLibraryAsset: asset } });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAdding(false);
    }
  };





  return createPortal(
    <>
      {showCreateProject && (
        <div className="fixed inset-0 z-[100001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <CreateProjectModal
            closeModal={() => setShowCreateProject(false)}
            onProjectCreated={(newProject) => {
              // Add the new project to the list, close the create modal,
              // and immediately open the confirm dialog for it
              setProjects((prev) => [newProject, ...prev]);
              setShowCreateProject(false);
              setConfirmProject(newProject);
            }}
          />
        </div>
      )}
      <div className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="relative flex flex-col w-full max-w-[1200px] rounded-xl overflow-hidden shadow-2xl"
          style={{ height: 'min(85vh, 780px)', minHeight: 400, background: '#1a1a1e', border: '1px solid #2a2a2e' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 sm:px-8 pt-5 sm:pt-7 pb-4 sm:pb-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <button type="button" onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-[#3a3a3e] text-white hover:border-white transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
              </button>
              <div>
                <h2 className="text-[16px] sm:text-[20px] font-bold text-white leading-tight">Add to Project</h2>
                <p className="text-[12px] sm:text-[13px] text-[#666] mt-0.5 hidden sm:block">Choose an existing project or create a new project</p>
              </div>
            </div>
            <button type="button" onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-[#3a3a3e] text-[#aaa] hover:text-white hover:border-white transition-colors text-[16px]">✕</button>
          </div>

          {/* Search + New project */}
          <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-8 pb-4 sm:pb-8">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input type="text" placeholder="Search by project name"
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full h-[44px] sm:h-[50px] pl-11 pr-5 rounded-full bg-[#1e1f22] border border-[#2a2a2e] text-[13px] sm:text-[14px] text-white placeholder:text-[#444] focus:outline-none focus:border-[#444]" />
            </div>
            <button type="button"
              onClick={() => setShowCreateProject(true)}
              className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 h-[44px] sm:h-[50px] rounded-[5px] bg-red-600 hover:bg-red-700 text-white text-[13px] sm:text-[14px] font-semibold transition-colors whitespace-nowrap">
              <span className="text-[18px] leading-none font-light">+</span>
              <span className="hidden sm:inline">New project</span>
            </button>
          </div>

          {/* Project grid */}
          <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-8 pb-6 sm:pb-8" style={{ minHeight: 0 }}>
            {loading ? (
              <div className="flex items-center justify-center h-40 text-[#555] text-[13px] gap-2">
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                Loading projects…
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <p className="text-[#555] text-[13px]">No projects found</p>
                {search && <button type="button" onClick={() => setSearch('')} className="text-[12px] text-red-500 hover:text-red-400">Clear search</button>}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4  p-4 sm:gap-5">
                {filtered.map((project) => {
                  const name = project.project_name || project.name || 'Untitled';
                  const thumb = project.th_image_url || project.thumbnail || project.coverImage;
                  const tags = [project.category, project.experience_type].filter(Boolean);
                  const isSelected = confirmProject?._id === project._id;
                  return (
                    <div key={project._id}
                      onClick={() => setConfirmProject(isSelected ? null : project)}
                      className={`flex flex-col rounded-xl overflow-hidden bg-[#1e1f22] cursor-pointer transition-all duration-150 ${isSelected ? 'ring-2 ring-red-500' : 'hover:ring-1 hover:ring-[#EE2B2A]'}`}>
                      <div className="w-full bg-[#111114] overflow-hidden aspect-video">
                        <img src={thumb} alt={name} className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }} />
                      </div>
                      <div className="px-3 sm:px-4 py-3 flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[12px] sm:text-[13px] font-bold text-white truncate">{name}</span>
                          <span className="text-[10px] sm:text-[11px] text-[#555] flex-shrink-0 hidden sm:block">{timeAgo(project.updatedAt || project.createdAt)}</span>
                        </div>
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {tags.map((t, ti) => (
                              <span key={`${project._id}-tag-${ti}`} className="px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] bg-[#2a2a2e] border border-[#333] text-[#888] capitalize">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Confirm overlay */}
          {confirmProject && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-xl p-4"
              onClick={() => setConfirmProject(null)}>
              <div className="relative flex flex-col items-center text-center rounded-2xl bg-[#1e1f22] px-6 py-6 shadow-2xl w-full max-w-[340px]"
                onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => setConfirmProject(null)}
                  className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full border border-[#3a3a3e] text-[#aaa] hover:text-white hover:border-white transition-colors text-[13px]">✕</button>
                <div className="w-[70px] h-[70px] rounded-full overflow-hidden bg-[#2a2a2e] border-2 border-[#3a3a3e] mb-4 flex items-center justify-center">
                  <img src={asset.thumbnail} alt={asset.name} className="w-full h-full object-contain"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }} />
                </div>
                <h3 className="text-[16px] font-bold text-white mb-2 leading-tight">Add {asset.name} to Project?</h3>
                <p className="text-[12px] text-[#888] leading-relaxed mb-5">
                  Are you sure you want to add <span className="text-white font-medium">'{asset.name}'</span> to this project?
                  This asset will be linked to the selected project and can be edited later.
                </p>
                <div className="flex gap-2 w-full">
                  <button type="button" onClick={() => setConfirmProject(null)}
                    className="flex-1 py-2 rounded-lg bg-[#2a2a2e] text-white text-[12px] font-semibold hover:bg-[#333] transition-colors">Cancel</button>
                  <button type="button" disabled={adding} onClick={handleConfirmAdd}
                    className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-semibold transition-colors disabled:opacity-60">
                    {adding ? 'Adding…' : 'Continue'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Asset Detail Modal ───
const AssetDetailModal = ({ asset, onClose }) => {
  const [showAddToProject, setShowAddToProject] = useState(false);

  if (!asset) return null;

  // Prefer streamUrl (compressed GLB) for the viewer — fall back to url
  const modelUrl = asset.streamUrl || asset.url || asset.asset_file_path || null;
  const is3D = !!(modelUrl && (
    modelUrl.toLowerCase().includes('.glb') ||
    modelUrl.toLowerCase().includes('.gltf') ||
    asset.type === '3D' ||
    asset.type === '3d'
  ));

  const techRows = [
    { label: 'Technique', value: asset.type ? `${asset.type} Model` : '3D Model' },
    { label: 'File Size', value: formatFileSize(asset.metadata?.fileSize) },
    { label: 'Polycount', value: formatPolyCount(asset.metadata?.polyCount) },
    { label: 'Format', value: asset.metadata?.format?.toUpperCase() || '—' },
  ];

  return createPortal(
    <>
      {showAddToProject && (
        <AddToProjectModal asset={asset} onClose={() => setShowAddToProject(false)} />
      )}

      <div className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="relative flex flex-col w-full max-w-[1200px] rounded-xl bg-[#1a1a1e] shadow-2xl overflow-hidden"
          style={{ height: 'min(85vh, 780px)', minHeight: 400 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-7 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-[#2a2a2e]">
            <h2 className="text-[16px] sm:text-[20px] font-semibold text-white truncate pr-4">{asset.name}</h2>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full border border-[#555] text-[#aaa] hover:text-white hover:border-white transition-colors text-[16px]"
            >✕</button>
          </div>

          {/* Body: viewer + details — side-by-side on md+, stacked on mobile */}
          <div className="flex flex-col md:flex-row flex-1 min-h-0 gap-4 p-4">

            {/* Viewer / thumbnail */}
            <div className="flex-[6] relative bg-[#080808] rounded-xl overflow-hidden min-h-[220px] md:min-h-0">
              {is3D ? (
                <>
                  <MeshPreviewViewer url={modelUrl} />

                </>
              ) : (
                <img
                  src={asset.thumbnail || asset.streamUrl}
                  alt={asset.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    if (e.currentTarget.src !== asset.streamUrl && asset.streamUrl) {
                      e.currentTarget.src = asset.streamUrl;
                    } else {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png';
                    }
                  }}
                />
              )}
            </div>

            {/* Details panel */}
            <div className="flex-[4] flex flex-col min-h-0 border border-[#3a3a3e] rounded-xl overflow-hidden">
              <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-4 sm:gap-5">
                <p className="text-[11px] font-semibold tracking-widest text-[#888] uppercase">Asset Details</p>

                <div>
                  <p className="text-[13px] text-[#aaa] mb-2">Category</p>
                  <span className="inline-block px-3 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white capitalize">
                    {asset.subtype || asset.type || '—'}
                  </span>
                </div>

                {asset.tags?.length > 0 && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {asset.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white capitalize">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[15px] font-semibold text-white mb-3">Technical information</p>
                  <table className="w-full border-collapse rounded-lg overflow-hidden">
                    <tbody>
                      {techRows.map((row, i) => (
                        <tr key={row.label} className={i % 2 === 0 ? 'bg-[#222226]' : 'bg-[#1e1e22]'}>
                          <td className="px-3 py-2.5 text-[12px] text-[#aaa] w-[45%]">{row.label}</td>
                          <td className="px-3 py-2.5 text-[12px] text-white font-medium">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-[#2a2a2e] bg-[#1a1a1e]">
                <button
                  type="button"
                  onClick={() => setShowAddToProject(true)}
                  className="w-full py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[14px] font-semibold transition-colors"
                >
                  Add to Project
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>,
    document.body
  );
};


// ─── Sketchfab card ───
const SketchfabCard = ({ model, onSelect }) => {
  const isPaid = model.isPaid === true || model.staffpickedAt != null;
  const thumb = model.thumbnails?.images?.[0]?.url ?? model.thumbnail ?? '';
  const name = model.name ?? 'Untitled';
  const poly = model.faceCount != null ? formatPolyCount(model.faceCount) : null;
  const size = model.archiveSize != null ? formatFileSize(model.archiveSize) : null;

  return (
    <div
      className="relative flex flex-col rounded-xl overflow-hidden bg-[#1e1f22] cursor-pointer hover:ring-1 hover:ring-[#EE2B2A] transition-all duration-150"
      onClick={() => onSelect?.(model)}
    >
      {/* Thumbnail */}
      <div className="relative w-full overflow-hidden p-4" style={{ height: 200 }}>
        <div className="relative w-full h-full rounded-[8px] overflow-hidden">
          <img
            src={thumb}
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://spatialgrid-uploads.s3.ap-south-1.amazonaws.com/uploads/library-assets/default-surface-thumbnail.png'; }}
          />
          {/* Premium / Free badge — inside the image so it clips correctly */}
          {isPaid ? (
            <div className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#1e1f22]/80 flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#F5A623]">
                <path d="M2 19l2-8 4 4 4-8 4 8 4-4 2 8H2z" />
              </svg>
            </div>
          ) : (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#1e1f22]/80 text-[10px] font-semibold text-green-400 shadow-lg">
              Free
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="px-4 py-3 flex flex-col gap-1">
        <span className="text-[13px] font-bold text-white truncate">{name}</span>
        {(poly || size) && (
          <span className="text-[11px] text-[#666]">
            {[poly, size].filter(Boolean).join(' • ')}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── Sketchfab Detail Modal ───
const SketchfabDetailModal = ({ model, onClose }) => {
  const [showAddToProject, setShowAddToProject] = useState(false);
  if (!model) return null;
  const isPaid = model.isPaid === true || model.staffpickedAt != null;

  // Normalise to asset shape AddToProjectModal expects
  const assetForProject = {
    _id: model.uid,
    name: model.name,
    thumbnail: model.thumbnail,
    source: 'sketchfab',
    ...model,
  };

  return createPortal(
    <>
      {showAddToProject && (
        <AddToProjectModal asset={assetForProject} onClose={() => setShowAddToProject(false)} />
      )}
      <div className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="relative flex flex-col w-full max-w-[1200px] rounded-xl bg-[#1a1a1e] shadow-2xl overflow-hidden"
          style={{ height: 'min(85vh, 790px)', minHeight: 400 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-8 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b border-[#2a2a2e]">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <h2 className="text-[16px] sm:text-[22px] font-semibold text-white truncate">{model.name}</h2>
              {isPaid ? (
                <div className="flex-shrink-0 flex items-center gap-1.5 px-2 sm:px-3 py-1 rounded-full bg-[#2a2a2e] border border-[#3a3a3e]">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#F5A623]">
                    <path d="M2 19l2-8 4 4 4-8 4 8 4-4 2 8H2z" />
                  </svg>
                  <span className="text-[10px] sm:text-[11px] text-[#F5A623] font-semibold">Premium</span>
                </div>
              ) : (
                <span className="flex-shrink-0 px-2 sm:px-2.5 py-0.5 rounded-full bg-green-500/10 text-[10px] sm:text-[11px] text-green-400 font-semibold">Free</span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full border border-[#555] text-[#aaa] hover:text-white hover:border-white transition-colors text-[16px]"
            >✕</button>
          </div>

          {/* Body — side-by-side on md+, stacked on mobile */}
          <div className="flex flex-col md:flex-row flex-1 min-h-0 gap-4 p-4">

            {/* Sketchfab iframe viewer */}
            <div className="flex-[6] relative bg-[#080808] rounded-xl overflow-hidden min-h-[220px] md:min-h-0">
              <iframe
                src={model.embedUrl}
                title={model.name}
                className="w-full h-full"
                allow="autoplay; fullscreen; xr-spatial-tracking"
                allowFullScreen
                style={{ border: 0 }}
              />
              <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm text-[11px] text-[#aaa] font-medium pointer-events-none">
                Powered by Sketchfab
              </div>
            </div>

            {/* Details panel */}
            <div className="flex-[4] flex flex-col min-h-0 border border-[#3a3a3e] rounded-xl overflow-hidden">
              <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-4 sm:gap-5">
                <p className="text-[11px] font-semibold tracking-widest text-[#888] uppercase">Model Details</p>

                {model.author && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">Author</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#2a2a2e] border border-[#3a3a3e] flex items-center justify-center text-[12px] text-white font-semibold">
                        {(model.author.displayName ?? model.author.username ?? 'U')[0].toUpperCase()}
                      </div>
                      <span className="text-[13px] text-white font-medium">
                        {model.author.displayName ?? model.author.username ?? 'Unknown'}
                      </span>
                    </div>
                  </div>
                )}

                {model.license && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">License</p>
                    <span className="inline-block px-3 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white">
                      {model.license}
                    </span>
                  </div>
                )}

                {model.tags?.length > 0 && (
                  <div>
                    <p className="text-[13px] text-[#aaa] mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {model.tags.slice(0, 12).map((tag, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-md bg-[#2a2a2e] border border-[#3a3a3e] text-[12px] text-white lowercase">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[15px] font-semibold text-white mb-3">Statistics</p>
                  <table className="w-full border-collapse rounded-lg overflow-hidden">
                    <tbody>
                      {[
                        { label: 'Views', value: model.viewCount?.toLocaleString() ?? '—' },
                        { label: 'Likes', value: model.likeCount?.toLocaleString() ?? '—' },
                        { label: 'Downloads', value: model.downloadCount?.toLocaleString() ?? '—' },
                        { label: 'Downloadable', value: model.isDownloadable ? 'Yes' : 'No' },
                      ].map((row, i) => (
                        <tr key={row.label} className={i % 2 === 0 ? 'bg-[#222226]' : 'bg-[#1e1e22]'}>
                          <td className="px-3 py-2.5 text-[12px] text-[#aaa] w-[45%]">{row.label}</td>
                          <td className="px-3 py-2.5 text-[12px] text-white font-medium">{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {model.description && (
                  <div>
                    <p className="text-[15px] font-semibold text-white mb-2">Description</p>
                    <p className="text-[12px] text-[#aaa] leading-relaxed whitespace-pre-wrap">{model.description}</p>
                  </div>
                )}
              </div>

              <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-[#2a2a2e] bg-[#1a1a1e]">
                <button
                  type="button"
                  onClick={() => setShowAddToProject(true)}
                  className="w-full py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-[14px] font-semibold transition-colors"
                >
                  Add to Project
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Library page ───
const Library = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const [isSortOpen, setIsSortOpen] = useState(false);

  // Sentinels
  const sentinelRef = useRef(null);
  const sfSentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const sfFetchingRef = useRef(false);

  const isSketchfab = sortBy === 'sketchfab';
  const isSpatialgrid = sortBy === 'spatialgrid';
  const isRelevance = sortBy === 'relevance';
  // show library cards when relevance or spatialgrid
  const showLibrary = isRelevance || isSpatialgrid;
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Relevance';
  const { category } = useParams();
  const dispatch = useDispatch();

  // show sketchfab cards ONLY for 3D Assets category (never for Templates, Scenes, etc.)
  const showSketchfab = (isRelevance || isSketchfab) && (category || LibraryCategories[0]?.category) === '3D';

  const {
    liabrary_cards,
    liabrary_loading,
    liabrary_loading_more,
    current_page,
    has_more,
    sketchfab_cards,
    sketchfab_loading,
    sketchfab_loading_more,
    sketchfab_has_more,
    sketchfab_cursor,
  } = useSelector((state) => state.liabrary);

  const currentCategory = category || LibraryCategories[0]?.category;

  // ── Build API params from category + active filters + search ──────────
  const buildParams = useCallback((page = 1) => {
    const params = { page, limit: 24 };

    // Base type comes from the route category
    if (currentCategory) params.type = currentCategory;

    // categories filter overrides type (e.g. "Scene", "Object", "Character", "Effect")
    if (activeFilters?.categories?.length === 1) {
      params.type = activeFilters.categories[0];
    }

    // industry — API accepts a single value; if multiple selected, send the first
    // The filter chips let users remove each one individually
    if (activeFilters?.industries?.length) {
      params.industry = activeFilters.industries[0];
    }

    // Asset size → derive fileSizeMin and fileSizeMax from selected ranges.
    // When multiple ranges are selected we union them:
    //   fileSizeMin = smallest lower-bound across selected ranges
    //   fileSizeMax = largest upper-bound across selected ranges
    if (activeFilters?.assetSizes?.length) {
      let minBytes = Infinity;
      let maxBytes = 0;
      activeFilters.assetSizes.forEach((label) => {
        const opt = ASSET_SIZE_OPTIONS.find((o) => o.label === label);
        if (!opt) return;
        if (opt.minBytes < minBytes) minBytes = opt.minBytes;
        if (opt.maxBytes > maxBytes) maxBytes = opt.maxBytes;
      });
      if (minBytes !== Infinity) params.fileSizeMin = minBytes;
      if (maxBytes > 0 && maxBytes !== Infinity) params.fileSizeMax = maxBytes;
    }

    // Search query (debounced)
    if (debouncedQuery.trim()) params.search = debouncedQuery.trim();

    return params;
  }, [currentCategory, activeFilters, debouncedQuery]);

  // ── Build Sketchfab params ────────────────────────────────────────────
  const buildSketchfabParams = useCallback((cursor = null, isFirstPage = true) => {
    // Base query: user search text OR route category as fallback
    const baseQ = debouncedQuery.trim() || currentCategory || '';

    // Industry → enrich the search query (Sketchfab has no industry param)
    const industryTerms = activeFilters?.industries?.length
      ? activeFilters.industries.join(' ')
      : '';

    // Build final q by combining base query + industry terms
    const q = [baseQ, industryTerms].filter(Boolean).join(' ');

    // Categories filter → Sketchfab categories param (lowercase slug)
    // Map our display labels to Sketchfab category slugs
    const SKETCHFAB_CATEGORY_SLUGS = {
      'scene': 'architecture',
      'object': 'science-technology',
      'character': 'characters-creatures',
      'effect': 'science-technology',
    };
    const cats = activeFilters?.categories?.length
      ? activeFilters.categories
        .map((c) => SKETCHFAB_CATEGORY_SLUGS[c.toLowerCase()] ?? c.toLowerCase())
        .join(',')
      : '';

    // Asset size → Sketchfab has no size filter; skip (client-side not feasible
    // without metadata, so we just ignore size ranges for Sketchfab results)

    return { q, categories: cats, cursor, isFirstPage };
  }, [debouncedQuery, currentCategory, activeFilters]);

  // ── Fetch on mode / filter / search / category change ────────────────
  useEffect(() => {
    // Library fetch — runs for Relevance and Spatial Grid
    if (showLibrary) {
      fetchingRef.current = false;
      dispatch(reset_library());
      dispatch(fetch_library_page(buildParams(1)));
    }
    // Sketchfab fetch — ONLY for 3D Assets category, never for any other category
    // currentCategory is included as a direct dependency to avoid stale closure issues
    if (showSketchfab && currentCategory === '3D') {
      sfFetchingRef.current = false;
      dispatch(reset_sketchfab());
      dispatch(fetch_sketchfab_page(buildSketchfabParams(null, true)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sortBy, currentCategory, buildParams, buildSketchfabParams]);

  // ── Library infinite scroll ───────────────────────────────────────────
  useEffect(() => {
    if (!showLibrary) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && has_more && !liabrary_loading && !liabrary_loading_more && !fetchingRef.current) {
        fetchingRef.current = true;
        dispatch(fetch_library_page(buildParams(current_page + 1))).finally(() => { fetchingRef.current = false; });
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [dispatch, buildParams, has_more, liabrary_loading, liabrary_loading_more, current_page, showLibrary]);

  // ── Sketchfab infinite scroll — only for 3D Assets ───
  useEffect(() => {
    if (!showSketchfab || currentCategory !== '3D') return;
    const sentinel = sfSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && sketchfab_has_more && !sketchfab_loading && !sketchfab_loading_more && !sfFetchingRef.current) {
        sfFetchingRef.current = true;
        dispatch(fetch_sketchfab_page(buildSketchfabParams(sketchfab_cursor, false))).finally(() => { sfFetchingRef.current = false; });
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [dispatch, buildSketchfabParams, sketchfab_has_more, sketchfab_loading, sketchfab_loading_more, sketchfab_cursor, showSketchfab]);

  // ── Debounce search input ───
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery), 600);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // ── Filter handlers ──
  const handleApplyFilters = (filters) => {
    const isEmpty =
      filters.industries.length === 0 &&
      filters.categories.length === 0 &&
      filters.assetSizes.length === 0;
    setActiveFilters(isEmpty ? null : filters);
  };

  const removeFilter = (type, value) => {
    setActiveFilters((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, [type]: prev[type].filter((v) => v !== value) };
      return (updated.industries.length + updated.categories.length + updated.assetSizes.length) === 0
        ? null : updated;
    });
  };

  const totalActiveFilters = activeFilters
    ? activeFilters.industries.length + activeFilters.categories.length + activeFilters.assetSizes.length
    : 0;

  const filterChips = activeFilters ? [
    ...activeFilters.industries.map((v) => ({ type: 'industries', label: `Industry: ${v}`, value: v })),
    ...activeFilters.categories.map((v) => ({ type: 'categories', label: `Categories: ${v}`, value: v })),
    ...activeFilters.assetSizes.map((v) => ({ type: 'assetSizes', label: `Asset Size: ${v}`, value: v })),
  ] : [];

  const isLoading = (showLibrary && liabrary_loading && liabrary_cards.length === 0) ||
    (showSketchfab && sketchfab_loading && sketchfab_cards.length === 0);
  const cards = [...(showLibrary ? liabrary_cards : []), ...(showSketchfab ? sketchfab_cards : [])];

  return (
    <>
      {isFilterModalOpen && (
        <FilterModal onClose={() => setIsFilterModalOpen(false)} onApply={handleApplyFilters} initialFilters={activeFilters} />
      )}
      {selectedAsset && selectedAsset.source === 'sketchfab' && (
        <SketchfabDetailModal model={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}
      {selectedAsset && selectedAsset.source !== 'sketchfab' && (
        <AssetDetailModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}

      <div className="flex flex-col h-full min-h-0 gap-4">
        {/* ── Fixed top section: search + filters + sort ── */}
        <div className="flex-shrink-0 flex flex-col gap-4">
          {/* Search + filter — height matches h1 in sidebar (38px leading) */}
          <div className="flex flex-row items-center justify-end h-[38px] overflow-visible">
            <div className="flex items-center gap-2 overflow-visible">
              <Input
                leftIcon={<img className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4" src={iconsPath?.group98Image} alt="search icon" />}
                type="text"
                placeholder={window.innerWidth > 500 ? 'Search for any assets, templates or files' : 'Search...'}
                className="w-[200px] sm:w-[280px] md:w-[340px] lg:w-[380px] h-[38px] rounded-[100px] border border-[#5C5C5C] bg-[#141416] pl-11 pr-4 text-[13px] font-normal font-inter text-white placeholder:text-[#B8B8B8] placeholder:text-[13px] focus:outline-none focus:border-[#6B6B6B] focus:ring-0"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(true)}
                className="relative flex items-center justify-between w-[120px] h-[38px] px-4 rounded-[100px] border border-[#5C5C5C] bg-[#141416] text-white flex-shrink-0 overflow-visible"
              >
                <span className="text-[13px] font-normal text-[#B8B8B8]">Add filter</span>
                <img src={vector} alt="filter" className="text-[11px] text-[#B8B8B8]" />
                {/* {totalActiveFilters > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                    {totalActiveFilters}
                  </span>
                )} */}
              </button>
            </div>
          </div>

          {/* Filter chips + sort — only visible after filters are applied */}
          {totalActiveFilters > 0 && (
            <div className="flex items-center justify-between w-full min-h-[38px]">
              <div className="flex flex-wrap gap-2">
                {filterChips.map((chip) => (
                  <span
                    key={`${chip.type}-${chip.value}`}
                    className="flex items-center gap-1.5 px-3 rounded-[7px] text-[12px] text-white"
                    style={{ height: '38px', border: '1px solid #1E1F22', background: '#2F2F33' }}
                  >
                    {chip.label}
                    <button type="button" onClick={() => removeFilter(chip.type, chip.value)} className="text-[#aaa] hover:text-white leading-none">✕</button>
                  </span>
                ))}
              </div>
              <div className="relative ml-auto">
                <div className="flex items-center gap-1.5 text-[13px] cursor-pointer select-none" onClick={() => setIsSortOpen((p) => !p)}>
                  <span className="text-[#8A8A8E]">Sort by:</span>
                  <span className="text-white font-semibold">{sortLabel}</span>
                  <ChevronDown size={16} className={`text-[#8A8A8E] transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                </div>
                {isSortOpen && (
                  <div className="absolute right-0 top-full mt-2 z-50 w-40 rounded-xl bg-[#1E1F22] border border-[#3A3A3E] shadow-2xl overflow-hidden">
                    {SORT_OPTIONS.map((opt) => (
                      <button key={opt.value} type="button"
                        onClick={() => { setSortBy(opt.value); setIsSortOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-[13px] transition-colors hover:bg-[#2a2a2e] ${sortBy === opt.value ? 'text-white font-semibold' : 'text-[#aaa]'}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Scrollable cards area ── */}
        <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0 pr-1">

          {/* Skeleton */}
          {isLoading && cards.length === 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="w-full h-[260px] rounded-xl bg-[#1e1f22] animate-pulse" />
              ))}
            </div>
          )}

          {/* Empty */}
          {!isLoading && cards.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
              <p className="text-[#888] text-[15px]">No assets found.</p>
              {activeFilters && (
                <button type="button" onClick={() => setActiveFilters(null)} className="text-[12px] text-red-500 hover:text-red-400 underline">Clear all filters</button>
              )}
            </div>
          )}

          {/* Library grid — shown for Relevance + Spatial Grid */}
          {showLibrary && liabrary_cards.length > 0 && (
            <>
              <CardLayout showName="name" cards={liabrary_cards} showAll showLibraryMeta
                onCardSelect={(card) => setSelectedAsset(card)}
                gridClass="grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4"
                cardSize="w-full h-[260px]"
              />
              <div ref={sentinelRef} className="w-full h-4" />
              {liabrary_loading_more && (
                <div className="flex justify-center py-6">
                  <svg className="w-6 h-6 animate-spin text-[#555]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                </div>
              )}
              {!has_more && !liabrary_loading_more && liabrary_cards.length > 0 && isSpatialgrid && (
                <p className="text-center text-[12px] text-[#555] py-4">Showing all {liabrary_cards.length} assets</p>
              )}
            </>
          )}

          {/* Sketchfab grid — shown for Relevance + Sketchfab */}
          {showSketchfab && sketchfab_cards.length > 0 && (
            <>
              {isRelevance && liabrary_cards.length > 0 && (
                <p className="text-[12px] font-semibold text-[#555] uppercase tracking-widest py-2">From Sketchfab</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                {sketchfab_cards.map((model, i) => (
                  <SketchfabCard key={`sf-${model.uid ?? model.id ?? i}-${i}`} model={model} onSelect={(m) => setSelectedAsset({ ...m, source: 'sketchfab' })} />
                ))}
              </div>
              <div ref={sfSentinelRef} className="w-full h-4" />
              {sketchfab_loading_more && (
                <div className="flex justify-center py-6">
                  <svg className="w-6 h-6 animate-spin text-[#555]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                </div>
              )}
              {!sketchfab_has_more && !sketchfab_loading_more && sketchfab_cards.length > 0 && isSketchfab && (
                <p className="text-center text-[12px] text-[#555] py-4">Showing all {sketchfab_cards.length} models</p>
              )}
            </>
          )}

        </div>{/* end scrollable cards */}
      </div>
    </>
  );
};

export default Library;
